import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { shopApi } from '../../api/shop';
import { useAuth } from '../../auth/AuthProvider';
import { AddressFormFields } from '../../components/AddressFormFields';
import { RequireShop } from '../../components/ShopSelector';
import { Breadcrumb, SectionCard } from '../../components/PageChrome';
import { ErrorBanner, LoadingState, PageHeader } from '../../components/Layout';
import { useShop } from '../../context/ShopContext';
import { emptyAddress, formatMoney } from '../../utils/format';

const emptyLine = { variant_id: '', quantity: '1' };

export default function CreateOrderPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getToken } = useAuth();
  const { selectedShopId } = useShop();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    customer_id: searchParams.get('customer_id') || '',
    currency: 'USD',
    note: '',
    shipping_customer_address_id: '',
    billing_same_as_shipping: true,
    use_saved_address: true,
    items: [{ ...emptyLine }],
  });
  const [inlineAddress, setInlineAddress] = useState({
    ...emptyAddress,
    full_name: '',
    country_code: 'US',
  });

  const variantOptions = useMemo(() => {
    const options = [];
    products.forEach((product) => {
      (product.variants || []).forEach((variant) => {
        options.push({
          id: variant.id,
          label: `${product.name} · ${variant.sku || variant.id} · ${formatMoney(variant.price, variant.currency)}`,
        });
      });
    });
    return options;
  }, [products]);

  const loadData = useCallback(async () => {
    if (!selectedShopId) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const [customerData, productData] = await Promise.all([
        shopApi.listCustomers(token, selectedShopId),
        shopApi.listProducts(token, selectedShopId),
      ]);
      setCustomers(customerData);
      setProducts(productData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, selectedShopId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const loadAddresses = async () => {
      if (!form.customer_id || !selectedShopId) {
        setAddresses([]);
        return;
      }
      try {
        const token = await getToken();
        const data = await shopApi.listCustomerAddresses(token, selectedShopId, form.customer_id);
        setAddresses(data);
        const defaultShipping = data.find((a) => a.is_default_shipping) || data[0];
        if (defaultShipping) {
          setForm((prev) => ({
            ...prev,
            shipping_customer_address_id: String(defaultShipping.id),
          }));
          setInlineAddress((prev) => ({
            ...prev,
            full_name: `${defaultShipping.full_name}`,
          }));
        }
      } catch (err) {
        setError(err.message);
      }
    };
    loadAddresses();
  }, [form.customer_id, getToken, selectedShopId]);

  const updateLine = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    }));
  };

  const addLine = () => {
    setForm((prev) => ({ ...prev, items: [...prev.items, { ...emptyLine }] }));
  };

  const removeLine = (index) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const payload = {
        customer_id: Number(form.customer_id),
        currency: form.currency.toUpperCase(),
        note: form.note.trim() || null,
        billing_same_as_shipping: form.billing_same_as_shipping,
        items: form.items.map((item) => ({
          variant_id: Number(item.variant_id),
          quantity: Number(item.quantity),
        })),
      };

      if (form.use_saved_address && form.shipping_customer_address_id) {
        payload.shipping_customer_address_id = Number(form.shipping_customer_address_id);
      } else {
        payload.shipping_address = {
          ...inlineAddress,
          company: inlineAddress.company || null,
          phone: inlineAddress.phone || null,
          email: inlineAddress.email || null,
          line2: inlineAddress.line2 || null,
          state: inlineAddress.state || null,
        };
      }

      const order = await shopApi.createOrder(token, selectedShopId, payload);
      navigate(`/shop/orders?created=${order.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingState />;
  }

  return (
    <RequireShop>
      <Breadcrumb
        items={[
          { label: 'Orders', to: '/shop/orders' },
          { label: 'Create order' },
        ]}
      />

      <PageHeader
        title="Create order"
        description="Build a new order with line items and shipping details."
        actions={
          <Link to="/shop/orders" className="btn btn-secondary">
            Cancel
          </Link>
        }
      />

      <ErrorBanner message={error} />

      <form onSubmit={handleSubmit}>
        <div className="detail-grid">
          <SectionCard title="Order details">
            <div className="form-grid">
              <div className="form-field">
                <label htmlFor="order-customer">Customer</label>
                <select
                  id="order-customer"
                  value={form.customer_id}
                  onChange={(e) => setForm((p) => ({ ...p, customer_id: e.target.value }))}
                  required
                >
                  <option value="">Select customer</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.first_name} {customer.last_name} ({customer.email})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-grid two-col">
                <div className="form-field">
                  <label htmlFor="order-currency">Checkout currency</label>
                  <input
                    id="order-currency"
                    maxLength={3}
                    value={form.currency}
                    onChange={(e) => setForm((p) => ({ ...p, currency: e.target.value.toUpperCase() }))}
                    required
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="order-note">Internal note</label>
                  <input
                    id="order-note"
                    value={form.note}
                    onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
                  />
                </div>
              </div>

              <div className="line-items-editor">
                <strong>Line items</strong>
                {form.items.map((item, index) => (
                  <div className="line-item-row" key={`line-${index}`}>
                    <div className="form-field" style={{ margin: 0 }}>
                      <label>Variant</label>
                      <select
                        value={item.variant_id}
                        onChange={(e) => updateLine(index, 'variant_id', e.target.value)}
                        required
                      >
                        <option value="">Select variant</option>
                        {variantOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-field" style={{ margin: 0 }}>
                      <label>Qty</label>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateLine(index, 'quantity', e.target.value)}
                        required
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => removeLine(index)}
                      disabled={form.items.length === 1}
                      aria-label="Remove line"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button type="button" className="btn btn-secondary btn-sm" onClick={addLine}>
                  Add line item
                </button>
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Shipping">
            <div className="form-grid">
              <label className="checkbox-field">
                <input
                  type="checkbox"
                  checked={form.use_saved_address}
                  onChange={(e) => setForm((p) => ({ ...p, use_saved_address: e.target.checked }))}
                />
                Use saved customer address
              </label>

              {form.use_saved_address ? (
                <div className="form-field">
                  <label htmlFor="saved-address">Saved address</label>
                  <select
                    id="saved-address"
                    value={form.shipping_customer_address_id}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, shipping_customer_address_id: e.target.value }))
                    }
                    required={form.use_saved_address}
                  >
                    <option value="">Select address</option>
                    {addresses.map((address) => (
                      <option key={address.id} value={address.id}>
                        {address.label || address.line1} — {address.city}
                      </option>
                    ))}
                  </select>
                  {form.customer_id && addresses.length === 0 && (
                    <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: '0.5rem 0 0' }}>
                      No saved addresses.{' '}
                      <Link to={`/shop/customers/${form.customer_id}`}>Add one on the customer profile</Link>.
                    </p>
                  )}
                </div>
              ) : (
                <AddressFormFields form={inlineAddress} onChange={setInlineAddress} idPrefix="ship" />
              )}

              <label className="checkbox-field">
                <input
                  type="checkbox"
                  checked={form.billing_same_as_shipping}
                  onChange={(e) => setForm((p) => ({ ...p, billing_same_as_shipping: e.target.checked }))}
                />
                Billing same as shipping
              </label>
            </div>
          </SectionCard>
        </div>

        <div className="page-actions" style={{ marginTop: '1rem' }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Creating order…' : 'Create order'}
          </button>
        </div>
      </form>
    </RequireShop>
  );
}
